import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import { Outlet } from "react-router-dom";
import CircleDot from '../../assets/images/circle-dot.svg';
import Oval from '../../assets/images/Oval.svg';
import close from '../../assets/images/icons8-close.svg';

import SidebarMenu from '../common/sidebarMenu';
import "./Layout.scss";
import Header from '../common/Header';
import { API_ENDPOINTS } from "../../utils/apiEndpoints";


const Layout = () => {
  const elementRef = useRef(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false);
  const toast = useRef(null);
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const assignedPermission = useSelector(
    (state) => state?.common?.user?.app_permission_ids || []
  );

  // Preload logo images
  const logoUrls = useMemo(() => ({
    mini: `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.GET_COMPANY_LOGO}`,
    full: `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.GET_COMPANY_FULL_LOGO}`
  }), []);

  const items = [
    ...(assignedPermission.includes("Dashboard_PageView")
      ? [
          {
            label: "Dashboard",
            icon: "icon-smart-home",
            command: () => {
              navigate("/dashboard");
            },
            url: "/dashboard",
          },
        ]
      : []),
    ...(assignedPermission.includes("PatientVisit_PageView")
      ? [
          {
            label: "Patient Visits",
            icon: "icon-users",
            command: () => {
              navigate("/patientVisits");
            },
            url: "/patientVisits",
          },
        ]
      : []),
    ...(assignedPermission.includes("Patients_PageView")
      ? [
          {
            label: "Patients",
            icon: "icon-user",
            command: () => {
              navigate("/patients");
            },
            url: "/patients",
          },
        ]
      : []),

    ...(assignedPermission.includes("Logistics_PageView")
      ? [
          {
            label: "Logistics",
            icon: "icon-building-warehouse",
            command: () => {
              navigate("/logistics");
            },
            url: "/logistics",
          },
        ]
      : []),
    ...(assignedPermission.includes("Invoices_PageView")
      ? [
          {
            label: "Invoices",
            icon: "icon-file-invoice",
            command: () => {
              navigate("/invoices");
            },
            url: "/invoices",
          },
        ]
      : []),
    ...(assignedPermission.includes("Report_PageView")
      ? [
          {
            label: "Reports",
            icon: "icon-brand-google-analytics",
            iconShow: true,
            items: [
              ...(assignedPermission.includes("Donor_Report_PageView")
                ? [
                    {
                      label: "Donor",
                      icon: "icon-circle",

                      command: () => {
                        navigate("reports/donor");
                      },
                      url: "/reports/donor",
                    },
                  ]
                : []),
              // ...(assignedPermission.includes("Doctor_Report_PageView")
              //   ? [
              //       {
              //         label: "Doctor",
              //         icon: "icon-circle",
              //         command: () => {
              //           navigate("reports/doctor");
              //         },
              //         url: "/reports/doctor",
              //       },
              //     ]
              //   : []),
              ...(assignedPermission.includes("Tenant_Report_PageView")
                ? [
                    {
                      label: "Tenant",
                      icon: "icon-circle",
                      command: () => {
                        navigate("reports/tenant");
                      },
                      url: "/reports/tenant",
                    },
                  ]
                : []),
              ...(assignedPermission.includes("Camp_Manager_Report_PageView")
                ? [
                    {
                      label: "Camp Manager",
                      icon: "icon-circle",
                      command: () => {
                        navigate("reports/campManager");
                      },
                      url: "/reports/campManager",
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(assignedPermission.includes("Settings_PageView")
      ? [
          {
            label: "Settings",
            icon: "icon-settings",
            iconShow: true,
            items: [
              ...(assignedPermission.includes("User_PageView")
                ? [
                    {
                      label: "Users",
                      icon: "icon-circle",
                      command: () => {
                        navigate("/users");
                      },
                      url: "/users",
                    },
                  ]
                : []),
              ...(assignedPermission.includes("Camp_PageView")
                ? [
                    {
                      label: "Camps",
                      icon: "icon-circle",
                      command: () => {
                        navigate("/camp");
                      },
                      url: "/camp",
                    },
                  ]
                : []),
              ...(assignedPermission.includes("Organization_PageView")
                ? [
                    {
                      label: "Organizations",
                      icon: "icon-circle",
                      command: () => {
                        navigate("/organizations");
                      },
                      url: "/organizations",
                    },
                  ]
                : []),
              ...(assignedPermission.includes("RolesAndPermission_PageView")
                ? [
                    {
                      label: "Roles & Permission",
                      icon: "icon-circle",
                      command: () => {
                        navigate("/roles");
                      },
                      url: "/roles",
                    },
                  ]
                : []),
              ...(assignedPermission.includes("Donor_PageView")
                ? [
                    {
                      label: "Donors",
                      icon: "icon-circle",
                      command: () => {
                        navigate("/donors");
                      },
                      url: "/donors",
                    },
                  ]
                : []),

              ...(assignedPermission.includes("Notification_PageView")
                ? [
                    {
                      label: "Notifications",
                      icon: "icon-circle",
                      iconShow: true,
                      items: [
                        ...(assignedPermission.includes(
                          "Notification_Send_PageView"
                        )
                          ? [
                              {
                                label: "Send Notification",
                                icon: "icon-circle",
                                command: () => {
                                  navigate("/notifications/send");
                                },
                                url: "/notifications/send",
                              },
                            ]
                          : []),
                        ...(assignedPermission.includes(
                          "Notification_Template_PageView"
                        )
                          ? [
                              {
                                label: "Manage Notification",
                                icon: "icon-circle",
                                command: () => {
                                  navigate("/notifications/manage");
                                },
                                url: "/notifications/manage",
                              },
                            ]
                          : []),
                        ...(assignedPermission.includes(
                          "Notification_History_PageView"
                        )
                          ? [
                              {
                                label: "Notification History",
                                icon: "icon-circle",
                                command: () => {
                                  navigate("/notifications/history");
                                },
                                url: "/notifications/history",
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              ...(assignedPermission.includes("Notification_PageView")
                ? [
                    {
                      label: "System Settings",
                      icon: "icon-circle",
                      command: () => {
                        navigate("/systemsettings");
                      },
                      url: "/systemsettings",
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
  ];

  const checkScroll = useCallback(() => {
    const element = elementRef.current;
    if (element) {
      setHasVerticalScroll(element.scrollHeight > element.clientHeight);
    }
  }, []);

  useEffect(() => {
    const delayedCheck = setTimeout(() => checkScroll(), 100);
    return () => clearTimeout(delayedCheck);
  }, [location, checkScroll]);

  useEffect(() => {
    const handleResize = () => requestAnimationFrame(checkScroll);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [checkScroll]);

  useEffect(() => {
    setIsHovered(false);
    setIsExpanded(false);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => checkScroll());
    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, [checkScroll]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsHovered(false);
        if (!isDesktop) {
          setIsExpanded(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDesktop]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const togglesidemobile = () => {
    setIsHovered(true);
  };

  useEffect(() => {
    const handleResize = () => {
      const isNowDesktop = window.innerWidth >= 1024;
      setIsDesktop(isNowDesktop);
      if (!isNowDesktop) {
        setIsHovered(false);
        setIsExpanded(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseEnter = () => isDesktop && setIsHovered(true);
  const handleMouseLeave = () => isDesktop && setIsHovered(false);

  const closeSidebar = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsHovered(false);
    setIsExpanded(false);
    console.log("true");
  };

  return (
    <div className="Layout__main-container">
      <div
        className={`Layout__inner-container ${isExpanded ? "expanded" : ""}`}
      >
        <div
          className={`Layout__left-sec ${
            isExpanded || isHovered ? "expanded" : ""
          }`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={isDesktop ? undefined : togglesidemobile}
        >
          <header ref={sidebarRef}>
            <div className="flex align-item-center">
              <div className="Layout__left-header-logo-sec">
                <img 
                  src={logoUrls.mini} 
                  className={`miniLogo ${isExpanded || isHovered ? 'hidden' : 'visible'}`}
                  alt="logo"
                />
                <img 
                  src={logoUrls.full} 
                  className={`${isExpanded || isHovered ? 'visible' : 'hidden'}`}
                  alt="logo"
                />
              </div>

              {(isExpanded || isHovered) && (
                <div
                  className="Layout__left-header-circle-dot-sec"
                  onClick={toggleSidebar}
                >
                  <div className="md:block hidden">
                    {isExpanded ? (
                      <img
                        src={CircleDot}
                        alt="CircleDot"
                        className={`circle-dot-svg ${
                          isExpanded ? "rotated" : ""
                        }`}
                        style={{ transition: "transform 0.2s ease" }}
                      />
                    ) : (
                      <img
                        src={Oval}
                        alt="CircleDot"
                        className={`circle-dot-svg ${
                          isExpanded ? "rotated" : ""
                        }`}
                        style={{ transition: "transform 0.2s ease" }}
                      />
                    )}
                  </div>
                </div>
              )}

              {(isExpanded || isHovered) && (
                <div
                  className="Layout__left-header-circle-dot-sec"
                  onClick={closeSidebar}
                >
                  <div className="md:hidden block">
                    <img src={close} className={`close-svg`} alt="close" />
                  </div>
                </div>
              )}
            </div>
            <div className={` ${isExpanded || isHovered ? "px-2" : ""}`}>
              <SidebarMenu
                visible={true}
                menuItems={items}
                isExpanded={isExpanded || isHovered}
                onCloseSidebar={(e) => {
                  if (!isDesktop) {
                    if (e) {
                      e.stopPropagation();
                    }
                    setIsHovered(false);
                    setIsExpanded(false);
                  }
                }}
              />
            </div>
            <Toast ref={toast} />
          </header>
        </div>
        <div className="Layout__right-container">
          {(isExpanded || isHovered) && (
            <div className="sidebar-overlay" onClick={closeSidebar} />
          )}

          <div className="md:px-4 md:pt-3 px-3 pt-0">
            <div className="Patient__search-container">
              <Header />
            </div>
          </div>
          <main
            className="inner-container overflow-x-hidden"
            ref={elementRef}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
